<!-- Camera component using library from: https://github.com/VinceG/vue-web-cam -->
<template>
  <v-dialog
      content-class="mobile-cam"
      v-model="showDialog"
  >
    <v-card class="pa-0 card-wrapper" tile>
      <v-form ref="form">
        <v-card-title>
          <h5 class="mb-0">{{ $t('components:camera-upload-form.title') }}</h5>
          <v-spacer></v-spacer>

          <!-- Settings menu -->
          <v-menu
              v-model="camSettings"
              offset-y
              :close-on-click="false"
              :close-on-content-click="false"
              eager>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>

            <v-list>

              <!-- Document name field -->
              <v-list-item>
                <v-text-field
                    outlined
                    v-model="docName"
                    :label="$t('common:document-name')"
                    :rules="[ruleRequired($t('utils:rules.required'))]"
                />
              </v-list-item>

              <!-- Camera select field -->
              <v-list-item>
                <v-select
                    v-model="camera"
                    :items="devices"
                    item-text="label"
                    item-value="deviceId"
                    @change="storeDeviceId = true">
                  <v-item
                      v-for="device in devices"
                      :key="device.deviceId"
                      :value="device.deviceId"
                  >
                    {{ device.label }}
                  </v-item>
                </v-select>
              </v-list-item>

              <!-- Camera start/stop buttons -->
              <v-list-item>
                <v-btn class="mr-3 mb-3 mb-md-0" outlined :color="color" @click="onStart">
                  <v-icon left>play_arrow</v-icon>
                  Start
                </v-btn>
                <v-btn class="mr-3 mb-3 mb-md-0" outlined :color="color" @click="onStop">
                  <v-icon left>play_disabled</v-icon>
                  Stop
                </v-btn>
              </v-list-item>

              <!-- Close menu button -->
              <v-list-item>
                <v-spacer></v-spacer>
                <BaseButton
                    color="primary"
                    btn-text="Schließen"
                    @clickedButton="camSettings = false"
                />
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-title>

        <v-card-text v-if="showDialog">
          <div :class="mobilePortraitMode ? 'cam-wrapper' : 'cam-wrapper-landscape'">
            <WebCam
                ref="webcam"
                :key="webcamKey"
                :device-id="deviceId"
                @error="onError"
                @cameras="onCameras"
                @camera-change="onCameraChange"
                :height="cameraHeight"
                :resolution="resolution"
            />
            <v-btn icon large class="my-3" color="white" @click="onCapture">
              <v-icon>camera</v-icon>
            </v-btn>
          </div>

          <v-carousel v-if="images.length > 0">
            <v-carousel-item
                v-for="(image, i) in images" :key="i"
            >
              <div class="image-wrapper">
                <v-btn icon color="scaleRed" @click="images.splice(i, 1)">
                  <v-icon>close</v-icon>
                </v-btn>
                <v-img :src="image" contain max-height="500"/>
              </div>
            </v-carousel-item>
          </v-carousel>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <BaseButton
              color="primary"
              @clickedButton="uploadDocumentation"
              :btn-text="$t('buttons:save')"
          />

          <BaseButton
              color="grey"
              @clickedButton="resetSettings"
              :btn-text="$t('buttons:back')"
          />
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>
<script>
import BaseButton from "@/modules/core/components/Base/BaseButton";
import {WebCam} from "vue-web-cam";
import {ruleRequired} from "../services/rules";
import Documentation from "@/modules/book/data/documentation";
import Utils from "@/modules/core/services/utils";

export default {
  name: "MobileCameraUploadForm",
  components: {
    BaseButton,
    WebCam
  },
  props: {
    color: String,
    hideButton: Boolean,
    offlineMode: Boolean
  },
  data() {
    return {
      showDialog: false,
      showSnapshotError: false,

      docName: null,

      images: [],
      camera: null,
      deviceId: null,
      devices: [],

      camSettings: false,
      resolution: {
        height: 2080,
        width: 1560
      },

      mobilePortraitMode: null,
      webcamKey: 0,
      storeDeviceId: false,

      cameraHeight: null,

      presetDocuName: "",
      documentationType: "",
      rowToPutDocuments: null
    }
  },
  computed: {
    device: function () {
      return this.devices.find(n => n.deviceId === this.deviceId);
    }
  },
  methods: {
    ruleRequired,

    uploadDocumentation() {
      if (!this.$refs.form.validate()) {
        this.camSettings = true;
        window.scrollTo(0, 0);
        return
      }

      for (let i = 0; i < this.images.length; i++) {
        if (!this.images[i]) {
          this.showSnapshotError = true;
          return;
        }
      }

      this.showSnapshotError = false;

      let documents = [];
      for (let i = 0; i < this.images.length; i++) {
        let docu = new Documentation();

        //transform the base64 encoded image into a blob, then the blob into a file
        //have to substring the image string and remove the data-value at the beginning
        let imageBlob = Utils.base64toBlob(this.images[i].substring(23), "image/jpeg");
        docu.file = new File([imageBlob], "photo_" + Date.now() + ".jpeg", imageBlob);
        docu.type = this.documentationType;
        docu.src = this.images[i];
        docu.start = "";
        docu.end = "";

        // auto increment name if presetDocuName is set
        // but not for the first image, because we set that
        // when dialog opens
        if (this.presetDocuName && i > 0) {
          // extract name and number of presetDocuName e.g. "docuName_23"
          let name = this.docName.split('_')[0];
          let nameCounter = parseInt(this.docName.split('_')[1]);
          nameCounter += 1;

          this.docName = name + '_' + nameCounter;
        }

        docu.name = this.docName;

        if (this.offlineMode) {
          docu.toSave = true;
          docu.localId = Utils.generateUUIDv4();
        }

        documents.push(docu);
      }

      let response = {
        documents: documents,
        rowToPut: this.rowToPutDocuments
      }
      this.$emit("postPhotos", response);
      this.resetSettings();
    },

    resetSettings() {
      this.onStop();
      this.showDialog = false;
      this.showSnapshotError = false;
      this.alertText = "";
      this.alertType = "";
      this.docName = "";
      this.images = [];
      this.webcamKey = 0;
      this.$refs.form.resetValidation();
    },

    onCapture() {
      let cardWrappers = document.getElementsByClassName('card-wrapper');

      // add flash effect
      for (let i = 0; i < cardWrappers.length; i++) {
        cardWrappers[i].classList.add('shutter-click')
      }
      setTimeout(() => {
        // put photo to the front of array so it will be displayed in the front in the carousel
        this.images.unshift(this.$refs.webcam.capture());

        for (let i = 0; i < cardWrappers.length; i++) {
          cardWrappers[i].classList.remove('shutter-click');
        }
      }, 300)
    },

    onStop() {
      this.$refs.webcam.stop();
    },

    onStart() {
      this.$refs.webcam.start();
    },

    onError(error) {
      this.$logger.error(error);
    },

    onCameras(cameras) {
      this.devices = cameras;
    },

    onCameraChange(deviceId) {
      this.deviceId = deviceId;
      this.camera = deviceId;

      if (this.storeDeviceId) {
        this.$store.commit('book/onboarding/storeChosenCamera', deviceId)
        this.storeDeviceId = false;
      }
    },

    handleMobileOrientation() {
      const orientation = window.screen.orientation.type;
      if (orientation === "portrait-primary") {
        this.mobilePortraitMode = true;
      } else if (orientation === "landscape-primary") {
        this.mobilePortraitMode = false;
      } else {
        this.mobilePortraitMode = null;
      }
    },

    openCameraDialog(presetDocuName, documentationType, rowToPut) {
      this.presetDocuName = presetDocuName;
      this.documentationType = documentationType;
      this.rowToPutDocuments = rowToPut;
      this.showDialog = true;
    }
  },
  watch: {
    // if a camera has been selected
    camera (id) {
      this.deviceId = id;
    },

    devices() {

      let cam;
      if (this.$store.state.book.onboarding.chosenCamera) {
        cam = this.$store.state.book.onboarding.chosenCamera;
      } else {
        // Once we have a list select the first one
        cam = this.devices[0].deviceId;
      }

      if (cam) {
        this.camera = cam;
        this.deviceId = cam;
      }

      this.onStop();
      this.onStart()
    },

    showDialog(state) {
      if (state) {
        this.$nextTick(() => {
          //first check if the camera has been initialized
          if (this.$refs.webcam) {
            // if a device Id is in the store
            if (this.$store.state.book.onboarding.chosenCamera) {
              this.deviceId = this.$store.state.book.onboarding.chosenCamera;
            }

            // if a presetDocuName is set, set it to the docu name
            if (this.presetDocuName) {
              this.docName = this.presetDocuName;
            }

            //afterwards check the camera and autostart it
            this.onStart();
            this.handleMobileOrientation();
          }
        })
      }
    },

    mobilePortraitMode() {

      // if mobilePortraitMode true, rescale the resolution to portrait mode
      if (this.mobilePortraitMode) {
        this.$set(this.resolution, 'height', 2080)
        this.$set(this.resolution, 'width', 1560)
        this.cameraHeight = "100%";
      } else {
        // else, rescale the resolution to landscape mode
        this.$set(this.resolution, 'height', 1560)
        this.$set(this.resolution, 'width', 2080)
        this.cameraHeight =  "300px";
      }

      // force webcam component to rerender
      // so the "snapshot" canvas has the
      // right resolution
      // onStart and onStop did sadly not work
      if (this.showDialog) {
        this.webcamKey += 1;
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener('orientationchange', this.handleMobileOrientation)
    })
  },

  beforeDestroy() {
    window.removeEventListener('orientationchange', this.handleMobileOrientation)
  }
}
</script>
<style lang="scss">
.mobile-cam {
  border-radius: 0;
  margin: 0;
  max-height: 100%!important;

  .cam-wrapper {
    position: relative;
    width: 100%;
    margin-bottom: 20px;

    button {
      position: absolute;
      top: 80%;
      left: 50%;
      transform: translate(-50%, -50%);
      -ms-transform: translate(-50%, -50%);
      background-color: var(--v-primary-base);
    }
  }

  .cam-wrapper-landscape {
    position: relative;
    width: 100%;

    button {
      position: absolute;
      top: 50%;
      left: 90%;
      transform: translate(-50%, -90%);
      -ms-transform: translate(-50%, -90%);
      background-color: var(--v-primary-base);
    }
  }

  .image-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: block;
    margin-bottom: auto;
    margin-top: auto;

    .v-btn {
      position: absolute;
      z-index: 1;
      right: 0;
    }

    .v-image {
      display: block;
      margin: auto;
      width: 80%;

      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      transform-origin: top left; /* IE 10+, Firefox, etc. */
      -webkit-transform-origin: top left; /* Chrome */
      -ms-transform-origin: top left; /* IE 9 */
    }
  }
}

@keyframes cameraFlash {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}
.shutter-click {
  z-index: 500;
  animation-name: cameraFlash;
  animation-duration: 0.3s;
  animation-fill-mode: both;
}

.card-wrapper:before {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.card-wrapper.shutter-click:before {
  content: '';
  animation: cameraFlash .3s .3s alternate-reverse 1;
}
</style>